import { useEffect, useRef } from 'react'

interface SnapshotRegionOverlayProps {
  viewer: any
  isActive: boolean
  onRegionSelected: (bounds: any) => void
}

export const SnapshotRegionOverlay: React.FC<SnapshotRegionOverlayProps> = ({
  viewer,
  isActive,
  onRegionSelected
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const rectRef = useRef<HTMLDivElement>(null)
  const startPointRef = useRef<{ x: number; y: number } | null>(null)
  const isDraggingRef = useRef(false)

  useEffect(() => {
    if (!viewer || !isActive || !overlayRef.current) {
      return
    }

    const overlay = overlayRef.current
    const rect = rectRef.current

    const handleMouseDown = (e: MouseEvent) => {
      if (!isActive || !rect) return

      const bounds = overlay.getBoundingClientRect()
      const x = e.clientX - bounds.left
      const y = e.clientY - bounds.top

      startPointRef.current = { x, y }
      isDraggingRef.current = true

      // Show rectangle
      rect.style.display = 'block'
      rect.style.left = x + 'px'
      rect.style.top = y + 'px'
      rect.style.width = '0px'
      rect.style.height = '0px'

      e.preventDefault()
      e.stopPropagation()
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !startPointRef.current || !rect) return

      const bounds = overlay.getBoundingClientRect()
      const currentX = e.clientX - bounds.left
      const currentY = e.clientY - bounds.top

      const minX = Math.min(startPointRef.current.x, currentX)
      const minY = Math.min(startPointRef.current.y, currentY)
      const width = Math.abs(currentX - startPointRef.current.x)
      const height = Math.abs(currentY - startPointRef.current.y)

      rect.style.left = minX + 'px'
      rect.style.top = minY + 'px'
      rect.style.width = width + 'px'
      rect.style.height = height + 'px'

      e.preventDefault()
      e.stopPropagation()
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDraggingRef.current || !startPointRef.current || !rect) return

      const bounds = overlay.getBoundingClientRect()
      const currentX = e.clientX - bounds.left
      const currentY = e.clientY - bounds.top

      const minX = Math.min(startPointRef.current.x, currentX)
      const minY = Math.min(startPointRef.current.y, currentY)
      const maxX = Math.max(startPointRef.current.x, currentX)
      const maxY = Math.max(startPointRef.current.y, currentY)

      // Only process if there's actual area selected
      if (Math.abs(maxX - minX) > 10 && Math.abs(maxY - minY) > 10) {
        // Convert pixel coordinates to viewport coordinates
        const OpenSeadragon = (window as any).OpenSeadragon
        const topLeft = viewer.viewport.pointFromPixel(new OpenSeadragon.Point(minX, minY))
        const bottomRight = viewer.viewport.pointFromPixel(new OpenSeadragon.Point(maxX, maxY))

        const viewportBounds = {
          x: topLeft.x,
          y: topLeft.y,
          width: bottomRight.x - topLeft.x,
          height: bottomRight.y - topLeft.y
        }

        // Call callback
        onRegionSelected(viewportBounds)
      }

      // Reset
      rect.style.display = 'none'
      startPointRef.current = null
      isDraggingRef.current = false

      e.preventDefault()
      e.stopPropagation()
    }

    // Add event listeners
    overlay.addEventListener('mousedown', handleMouseDown)
    overlay.addEventListener('mousemove', handleMouseMove)
    overlay.addEventListener('mouseup', handleMouseUp)

    // Prevent viewer from handling these events
    overlay.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    return () => {
      overlay.removeEventListener('mousedown', handleMouseDown)
      overlay.removeEventListener('mousemove', handleMouseMove)
      overlay.removeEventListener('mouseup', handleMouseUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  if (!isActive) {
    return null
  }

  return (
    <div
      ref={overlayRef}
      className="absolute inset-0 cursor-crosshair"
      style={{
        pointerEvents: 'all',
        zIndex: 10000,
        backgroundColor: 'rgba(128, 0, 128, 0.05)' // Very light purple tint to show overlay is there
      }}
    >
      {/* Debug indicator in corner */}
      <div className="absolute top-2 left-2 bg-purple-600 text-white px-2 py-1 rounded text-xs font-bold pointer-events-none">
        SNAPSHOT MODE ACTIVE
      </div>

      {/* Selection rectangle */}
      <div
        ref={rectRef}
        className="absolute pointer-events-none"
        style={{
          display: 'none',
          border: '3px dashed #8B5CF6',
          backgroundColor: 'rgba(139, 92, 246, 0.15)',
          boxShadow: '0 0 20px rgba(139, 92, 246, 0.5)'
        }}
      />
    </div>
  )
}

export default SnapshotRegionOverlay
